import { useEffect, ReactNode, useState, useRef } from 'react'
import { createPortal } from 'react-dom'

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title: ReactNode
  children: ReactNode
  size?: 'sm' | 'md' | 'lg' | 'xl'
  // If true, modal takes full screen (useful for edit/create flows)
  fullScreen?: boolean
  // Optional custom header nodes (left and right). If provided, they'll be
  // rendered in the header area. Useful to inject back/save buttons.
  headerLeft?: ReactNode
  headerRight?: ReactNode
  // If true, align modal to the top on small screens (helps with mobile keyboards)
  mobileTopAligned?: boolean
}

export function Modal({ isOpen, onClose, title, children, size = 'md', fullScreen = false, headerLeft, headerRight, mobileTopAligned = true }: ModalProps) {
  const [isMobileView, setIsMobileView] = useState(false)

  useEffect(() => {
    const check = () => setIsMobileView(typeof window !== 'undefined' ? window.innerWidth < 768 : false)
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  const panelRef = useRef<HTMLDivElement | null>(null)

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Only on desktop view we transform Enter into Tab navigation
    if (isMobileView) return
    if (e.key !== 'Enter') return

    const target = e.target as HTMLElement | null
    if (!target) return

    const tag = (target.tagName || '').toLowerCase()

    // Allow Enter in textareas and contenteditable elements
    if (tag === 'textarea' || target.isContentEditable) return

    // Allow default behavior on actual buttons, anchors or explicit submit inputs
    if (tag === 'button') return
    if (tag === 'a') return
    if (tag === 'input') {
      const t = (target.getAttribute('type') || '').toLowerCase()
      if (t === 'submit' || t === 'button' || t === 'image') return
    }

    const root = panelRef.current
    if (!root) return

    const focusableSelector = 'a[href]:not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"]), textarea:not([disabled]):not([tabindex="-1"]), input:not([type="hidden"]):not([disabled]):not([tabindex="-1"]), select:not([disabled]):not([tabindex="-1"]), [tabindex]:not([tabindex="-1"])'
    const all = Array.from(root.querySelectorAll<HTMLElement>(focusableSelector))
      .filter(el => (el.offsetWidth > 0 || el.offsetHeight > 0 || el.getClientRects().length > 0))

    if (all.length === 0) return

    // Prefer the actual event target as the current index, fallback to document.activeElement
    const active = document.activeElement as HTMLElement | null
    let cur = all.indexOf(target)
    if (cur === -1 && active) cur = all.indexOf(active)
    if (cur === -1) cur = 0

    const dir = e.shiftKey ? -1 : 1
    let next = cur + dir
    if (next < 0) next = all.length - 1
    if (next >= all.length) next = 0

    e.preventDefault()
    e.stopPropagation()
    try {
      all[next].focus()
    } catch (err) {
      // ignore
    }
  }

  if (!isOpen) return null

  const effectiveFullScreen = fullScreen || isMobileView

  const sizeClasses = isMobileView
    ? {
        sm: 'max-w-md',
        md: 'max-w-2xl',
        lg: 'max-w-4xl',
        xl: 'max-w-6xl',
      }
    : {
        // On desktop, make sizes a bit larger to reduce scrolling
        sm: 'max-w-2xl',
        md: 'max-w-4xl',
        lg: 'max-w-6xl',
        xl: 'max-w-7xl',
      }

  const modalContent = (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 z-[9999] bg-black/50 dark:bg-black/70 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className={
          `fixed inset-0 z-[10000] overflow-y-auto flex justify-center ${effectiveFullScreen ? 'p-0' : 'p-4'}`
        }
        style={{ alignItems: (isMobileView && mobileTopAligned) ? 'flex-start' as const : 'center' as const }}
      >
        <div 
          ref={panelRef}
          onKeyDown={handleKeyDown}
          tabIndex={-1}
          className={
            effectiveFullScreen
              ? `relative w-full h-full sm:h-auto bg-white dark:bg-slate-800 shadow-2xl transform transition-all`
              : `relative w-full ${sizeClasses[size]} bg-white dark:bg-slate-800 rounded-xl shadow-2xl transform transition-all`
          }
          onClick={(e) => e.stopPropagation()}
          style={{ maxWidth: fullScreen ? '100%' : undefined }}
        >
          {/* Panel layout: header (sticky) + content (scrollable) */}
          <div className={fullScreen ? 'flex flex-col h-full' : ''}>
            <div className={`relative ${fullScreen ? '' : ''}`}>
              {/* Header (sticky when fullScreen) */}
              <div className={`w-full ${fullScreen ? 'sticky top-0 z-30' : ''} bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm border-b border-slate-200 dark:border-slate-700`}>
                <div className="relative flex items-center justify-between px-6 py-4">
                  <div className="flex items-center gap-2">
                    {headerLeft ? headerLeft : (
                      isMobileView ? (
                        <button
                          onClick={onClose}
                          className="text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-slate-100 transition-colors"
                          aria-label="Fechar"
                        >
                          <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      ) : null
                    )}

                  </div>

                  {/* Centered title */}
                  <div className="absolute left-1/2 transform -translate-x-1/2">
                    <div className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                      {title}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {headerRight}
                  </div>
                </div>
              </div>
            </div>

            {/* Content area */}
            <div className={`${fullScreen ? 'overflow-y-auto p-6 flex-1' : 'p-6 overflow-y-auto max-h-[85vh]'}`}>
              {children}
            </div>
          </div>
        </div>
      </div>
    </>
  )

  return createPortal(modalContent, document.body)
}
